import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HttpService } from '@Services/api/httpService';
import { RoutesMap } from '@Router/router.types';
import { DocVStatus } from '@Store/slices/ubo/createUboSlice';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';

const MAX_POLLING_TIME = 5400000;
const RETRY_DELAY = 2000;
const RETRY_TIMES = 2700;

export interface UboDocVStatusResponse {
  docVStatus: DocVStatus;
  onboardingStatus: string;
  token: string | null;
  secondaryAddress?: Record<string, unknown>;
  errorMessage?: string;
}

export const getUboDocVStatus = async (customerId: string): Promise<UboDocVStatusResponse> => {
  const url = `v1/onboarding/secondary/${customerId}/docVStatus`;
  const { data } = await HttpService.get<UboDocVStatusResponse>(url);
  return data;
};

export interface UseUboDocVStatusPollingParams {
  customerId: string;
}

export const useUboDocVStatusPolling = (params: UseUboDocVStatusPollingParams) => {
  const { customerId } = params;
  const setDocVData = useBoundStore((state) => state.setDocVData);
  const { navigateToSpecificPage } = useNavigationManager();
  const startTimeRef = useRef<number | null>(null);
  const lastStatusRef = useRef<DocVStatus | null>(null);
  const [maxPollingTime] = useState(MAX_POLLING_TIME);
  const [retryTimes] = useState(RETRY_TIMES);

  useEffect(() => {
    startTimeRef.current = Date.now();
  }, []);

  return useQuery({
    queryFn: () => getUboDocVStatus(customerId),
    queryKey: ['getUboDocVStatus', customerId],
    retry: retryTimes,
    retryDelay: RETRY_DELAY,
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchInterval: (query) => {
      const elapsedTime = startTimeRef.current ? Date.now() - startTimeRef.current : 0;

      if (elapsedTime >= maxPollingTime) {
        navigateToSpecificPage(RoutesMap.UNDER_REVIEW);
        return false;
      }

      const { data } = query.state;
      if (data) {
        return handleUboDocVStatus(data, navigateToSpecificPage, setDocVData, lastStatusRef);
      }

      return RETRY_DELAY;
    },
  });
};

export const handleUboDocVStatus = (
  data: UboDocVStatusResponse,
  navigateToSpecificPage: (route: RoutesMap) => void,
  setDocVData: (data: { status: DocVStatus }) => void,
  lastStatusRef: React.MutableRefObject<DocVStatus | null>
): number | false => {
  const { docVStatus } = data;
  const hasStatusChanged = lastStatusRef.current !== docVStatus;

  if (hasStatusChanged) {
    lastStatusRef.current = docVStatus;
    setDocVData({ status: docVStatus });
  }

  if (docVStatus === DocVStatus.APPROVED) {
    navigateToSpecificPage(RoutesMap.INVITE_TEAM_MEMBER_FINISHED);
    return false;
  }

  if (docVStatus === DocVStatus.REJECTED) {
    navigateToSpecificPage(RoutesMap.VERIFICATION_FAILED);
    return false;
  }

  if (docVStatus === DocVStatus.WAITING || docVStatus === DocVStatus.MANUAL_REVIEW) {
    return RETRY_DELAY;
  }

  return RETRY_DELAY;
};
